import { useCallback, useEffect, useRef } from 'react';
import { AnimatePresence } from 'motion/react';
import { useLocation } from 'react-router';
import LexCounselLauncher from '../editor/LexCounselLauncher';
import { useAuth } from '../../contexts/AuthContext';
import { useSystem } from '../../contexts/SystemContext';
import { useFeatureGatingSafe } from '../../contexts/FeatureGatingContext';
import { useToast } from '../../contexts/ToastContext';
import { useWorkspaceTabs } from '../../store/workspaceTabsStore';
import { useLexCounselStore } from '../../store/lexCounselStore';
import { useLexCounselChatActions } from '../../hooks/useLexCounselChat';
import {
  inferScreenFromRoute,
  buildCaseWorkspaceContext,
  buildDashboardWorkspaceContext,
  buildLibraryWorkspaceContext,
} from '../../lib/workspaceContext';
import type { LexCounselSendDetail } from '../../lib/lexCounselSend';
import { loadConversations, loadLegacyAiChats } from '../../lib/lexCounselConversations';
/**
 * Global LexCounsel host — persistent panel + launcher on every screen.
 */
export default function LexCounselGlobalHost() {
  const { user, activeWorkspaceId } = useAuth();
  const { config } = useSystem();
  const { check } = useFeatureGatingSafe();
  const { showError } = useToast();
  const location = useLocation();
  const { tabs, activeTabId } = useWorkspaceTabs();

  const isOpen = useLexCounselStore((s) => s.isOpen);
  const openPanel = useLexCounselStore((s) => s.openPanel);
  const isThinking = useLexCounselStore((s) => s.isThinking);
  const setIsThinking = useLexCounselStore((s) => s.setIsThinking);
  const messages = useLexCounselStore((s) => s.messages);
  const setMessages = useLexCounselStore((s) => s.setMessages);
  const inputDraft = useLexCounselStore((s) => s.inputDraft);
  const setInputDraft = useLexCounselStore((s) => s.setInputDraft);
  const conversationId = useLexCounselStore((s) => s.currentConversationId);
  const setConversationId = useLexCounselStore((s) => s.setCurrentConversationId);
  const setConversations = useLexCounselStore((s) => s.setConversations);
  const setWorkspaceSnapshot = useLexCounselStore((s) => s.setWorkspaceSnapshot);
  const setLastDiagnostics = useLexCounselStore((s) => s.setLastDiagnostics);
  const workspace = useLexCounselStore((s) => s.workspace);

  const { sendMessage, stopGeneration, loadConversationList } = useLexCounselChatActions();

  const activeTab = tabs.find((t) => t.id === activeTabId || t.id === location.pathname);

  useEffect(() => {
    const screen = inferScreenFromRoute(location.pathname);
    const wsId = activeWorkspaceId ?? undefined;
    const route = location.pathname;
    const onCaseRoute = route.startsWith('/cases/') && route !== '/cases';
    const onDraftRoute = route.startsWith('/drafts/');

    if (activeTab?.type === 'case' && activeTab.entityId && onCaseRoute) {
      setWorkspaceSnapshot({
        screen: 'case',
        workspaceId: wsId,
        caseId: activeTab.entityId,
        draftId: undefined,
        draftTitle: undefined,
        dashboardBrief: undefined,
        workspaceContext: buildCaseWorkspaceContext({
          workspaceId: wsId,
          caseId: activeTab.entityId,
          route,
        }),
      });
    } else if (
      (activeTab?.type === 'draft' || activeTab?.type === 'document') &&
      activeTab.entityId &&
      onDraftRoute
    ) {
      // Route metadata only — LexDraftEditor (active workspace tab) owns case/draft context.
      setWorkspaceSnapshot({
        screen: 'draft_editor',
        workspaceId: wsId,
        draftId: activeTab.entityId,
        dashboardBrief: undefined,
      });
    } else if (screen === 'library') {
      setWorkspaceSnapshot({
        screen: 'library',
        workspaceId: wsId,
        caseId: undefined,
        caseTitle: undefined,
        caseData: null,
        proceedings: undefined,
        nextHearingDate: undefined,
        draftId: undefined,
        draftTitle: undefined,
        draftText: undefined,
        dashboardBrief: undefined,
        workspaceContext: buildLibraryWorkspaceContext({ workspaceId: wsId, route }),
      });
    } else {
      setWorkspaceSnapshot({
        screen,
        workspaceId: wsId,
        caseId: undefined,
        caseTitle: undefined,
        caseData: null,
        proceedings: undefined,
        nextHearingDate: undefined,
        draftId: undefined,
        draftTitle: undefined,
        draftText: undefined,
        selectedText: undefined,
        workspaceContext: buildDashboardWorkspaceContext({ workspaceId: wsId, route }),
      });
    }
  }, [
    location.pathname,
    activeTab,
    activeWorkspaceId,
    setWorkspaceSnapshot,
  ]);

  useEffect(() => {
    if (!isOpen || !user?.uid || !activeWorkspaceId) return;
    void loadConversationList(activeWorkspaceId, user.uid, workspace.caseId ?? null).then(setConversations);
  }, [isOpen, user?.uid, activeWorkspaceId, workspace.caseId, loadConversationList, setConversations]);

  useEffect(() => {
    const handler = async (e: Event) => {
      const detail = (e as CustomEvent<LexCounselSendDetail>).detail;
      const text = (detail?.text ?? inputDraft).trim();
      if (!text || isThinking || !user?.uid || !activeWorkspaceId) return;

      window.dispatchEvent(new CustomEvent('lexcounsel-prepare-send', { detail: { text } }));
      const freshWorkspace = useLexCounselStore.getState().workspace;
      const freshMessages = useLexCounselStore.getState().messages;

      setInputDraft('');
      setIsThinking(true);
      try {
        await sendMessage({
          text,
          slashCommand: detail?.command ?? null,
          scope: detail?.scope,
          commandId: detail?.commandId,
          workspace: freshWorkspace,
          messages: freshMessages,
          workspaceId: activeWorkspaceId,
          userId: user.uid,
          conversationId,
          onMessages: setMessages,
          onConversationId: setConversationId,
          onDiagnostics: setLastDiagnostics,
        });
        const list = await loadConversationList(activeWorkspaceId, user.uid, workspace.caseId ?? null);
        setConversations(list);
      } finally {
        setIsThinking(false);
      }
    };
    window.addEventListener('lexcounsel-send', handler);
    return () => window.removeEventListener('lexcounsel-send', handler);
  }, [
    inputDraft,
    isThinking,
    user?.uid,
    activeWorkspaceId,
    workspace,
    messages,
    conversationId,
    sendMessage,
    setInputDraft,
    setIsThinking,
    setMessages,
    setConversationId,
    setLastDiagnostics,
    loadConversationList,
    setConversations,
  ]);

  const isThinkingRef = useRef(isThinking);
  isThinkingRef.current = isThinking;

  useEffect(() => {
    const onStop = () => {
      if (!isThinkingRef.current) return;
      stopGeneration();
      setIsThinking(false);
      requestAnimationFrame(() => {
        window.dispatchEvent(new CustomEvent('lexcounsel-focus-composer'));
      });
    };
    window.addEventListener('lexcounsel-stop', onStop);
    return () => window.removeEventListener('lexcounsel-stop', onStop);
  }, [stopGeneration, setIsThinking]);

  useEffect(() => {
    if (!conversationId || !activeWorkspaceId) return;
    // Creating a chat mid-send sets conversationId and would reload persisted
    // history (user-only), wiping the streaming AI placeholder + loading badge.
    if (useLexCounselStore.getState().isThinking) return;

    let cancelled = false;
    void (async () => {
      let chats = await loadConversations(activeWorkspaceId, user!.uid, workspace.caseId ?? null);
      if (chats.length === 0 && workspace.caseId) {
        chats = await loadLegacyAiChats(activeWorkspaceId, user!.uid, workspace.caseId);
      }
      if (cancelled) return;
      if (useLexCounselStore.getState().isThinking) return;
      const found = chats.find((c) => c.id === conversationId);
      if (found?.history) setMessages(found.history);
    })();
    return () => {
      cancelled = true;
    };
  }, [conversationId, activeWorkspaceId, workspace.caseId, user, setMessages]);

  const handleOpen = useCallback(async () => {
    const gate = await check('lexCounsel');
    if (!gate.allowed) {
      showError(gate.reason ?? 'LexCounsel is not available on your plan.');
      return;
    }
    openPanel();
  }, [check, showError, openPanel]);

  if (!config.enableAIFeatures) return null;

  return (
    <>
      <AnimatePresence>
        {!isOpen && (
          <div className="fixed bottom-24 md:bottom-6 right-4 md:right-6 z-[65]">
            <LexCounselLauncher
              onClick={handleOpen}
              active={isThinking}
              className="shadow-[0_8px_30px_rgb(0,0,0,0.12)]"
            />
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
